<template>
  <div class="poster-cart">
    <pui-common-spinner
      v-show="isPerformingRequest"
      :message="currentMessage"
      fullscreen
      hideBackgroundCircle
    />
    <!-- Cart dialog -->
    <pui-dialog ref="cartConfirmDialog" :showCloseButton="false" transition="slide-up">
      <h3 v-if="currentCartAction === CartAction.DELETE_SELECTION" class="message">
        <span class="mdi mdi-trash-can" />
        {{
          $tc('poster.creation.cart.delete.selection.message', selectedSignageItems.length, {
            count: selectedSignageItems.length,
          })
        }}
      </h3>
      <h3 v-if="currentCartAction === CartAction.DELETE_ALL" class="message">
        <span class="mdi mdi-trash-can" />
        {{ $t('poster.creation.cart.delete.all.message') }}
      </h3>
      <pui-button-list>
        <pui-button @click="closeCartConfirmationDialog()">
          {{ $t('poster.creation.cart.delete.cancel') }}
        </pui-button>
        <pui-button @click="executeCartAction()">
          {{ $t('poster.creation.cart.delete.confirm') }}
        </pui-button>
      </pui-button-list>
    </pui-dialog>

    <!-- Search dialog -->
    <pui-dialog
      ref="searchDialog"
      :title="
        currentSearchParams != null &&
        currentSearchParams.searchDefinition != null &&
        currentSearchParams.searchDefinition.modes != null &&
        currentSearchParams.searchDefinition.modes.length > 0 &&
        currentSearchParams.searchDefinition.modes[0] != null &&
        currentSearchParams.searchDefinition.modes[0].search != null
          ? piivoTranslateLabel(currentSearchParams.searchDefinition.modes[0].search.title)
          : ''
      "
      :showCloseButton="false"
      @close="closeSearchDialog"
      class="import-dialog"
      transition="slide-bottom"
      width="100%"
    >
      <div slot="dialog-action" class="dialog-action">
        <pui-button @click="closeSearchDialog" outline variant="secondary">
          {{ $t('poster.creation.search.cancel') }}
        </pui-button>
        <pui-button
          :disabled="!selectedSearchedItems || !selectedSearchedItems.length"
          @click="setSearchResult()"
          variant="priamry"
        >
          {{ $t('poster.creation.search.confirm') }}
        </pui-button>
      </div>
      <poster-search-panel
        ref="searchPanel"
        v-if="currentSearchParams"
        :searchFunction="search"
        :attributeMap="attributeMap"
        :searchConfiguration="currentSearchParams.searchDefinition"
        :templateId="currentTemplate != null ? currentTemplate.templateId : null"
        @selectedSearchedItemsChanged="updateSelectedSearchedItems"
        @searchResultsValidated="addToSearchSelection"
        @changeSearchMode="changeSearchMode"
      >
        <template
          v-if="
            currentSearchParams &&
              currentSearchParams.searchDefinition &&
              currentSearchParams.searchDefinition.multiSelect &&
              currentSearchParams.searchDefinition.summary
          "
          #searchSummaryPanel="{ showSearchResultsPanel }"
        >
          <poster-search-summary
            :searchDefinition="currentSearchParams.searchDefinition"
            :minimized="showSearchResultsPanel"
            v-model="selectedSearchedItems"
          ></poster-search-summary>
        </template>
      </poster-search-panel>
    </pui-dialog>

    <pui-action-bar class="poster-cart-action-bar">
      <template #left>
        <pui-button
          v-if="selectedSignageItems.length"
          id="quick-btn-deselect-all"
          :flat="true"
          @click="selectedSignageItems = []"
          :class="{
            'btn-cart-action pui-action-bar__button': true,
            'action-border': !showCart,
          }"
          variant="primary"
        >
          <i class="mdi mdi-close deselect-all-icon"></i>
        </pui-button>
        <pui-button
          v-else
          id="quick-btn-toggle-cart"
          v-track:click="Tracking.QUICK_TOGGLE_CART"
          :flat="true"
          :disabled="!signageItems.length || !!openPosterTypeDialog"
          @click="toggleCart"
          :class="{
            'btn-cart-action pui-action-bar__button': true,
            backwards: showCart,
            'action-border': !showCart,
          }"
          variant="primary"
        >
          <pui-badge
            :content="showCart ? '' : signageItems.length"
            :bgColor="showCart ? `'initial'` : 'var(--color-error-background)'"
            color="var(--color-initial)"
            offsetY="8px"
          >
            <!-- mdi-forwardburger -->
            <svg viewBox="0 0 24 24" class="show-cart-icon">
              <path
                d="M19,13H3V11H19L15,7L16.4,5.6L22.8,12L16.4,18.4L15,17L19,13M3,6H13V8H3V6M13,16V18H3V16H13Z"
              />
            </svg>
          </pui-badge>
        </pui-button>
        <transition name="expand-width">
          <pui-flex v-if="showCart" class="cart-details action-border" alignItems="center">
            <h3 class="cart-title">
              {{
                selectedSignageItems.length
                  ? $tc('poster.creation.cart.title.selected', selectedSignageItems.length, {
                      count: selectedSignageItems.length,
                    })
                  : $tc('poster.creation.cart.title.total', signageItems.length, {
                      count: signageItems.length,
                    })
              }}
            </h3>

            <pui-menu-button
              :buttonProps="{ disabled: !!openPosterTypeDialog }"
              class="cart-actions-menu-button"
            >
              <template #default="{ closePopper }">
                <ul class="actions-list">
                  <li
                    v-if="selectedSignageItems.length"
                    @click="
                      openCartConfirmationDialog(CartAction.DELETE_SELECTION);
                      closePopper();
                    "
                    class="actions-list-item"
                  >
                    {{ $t('poster.creation.cart.menu.delete_selection') }}
                  </li>
                  <li
                    v-else
                    @click="
                      openCartConfirmationDialog(CartAction.DELETE_ALL);
                      closePopper();
                    "
                    class="actions-list-item"
                  >
                    {{ $t('poster.creation.cart.menu.delete_all') }}
                  </li>
                </ul>
              </template>
            </pui-menu-button>
          </pui-flex>
        </transition>
        <pui-button-list class="quick-action-list">
          <pui-button
            id="quick-choose-new-poster"
            @click="toggleNewPosterDialog"
            v-track:click="Tracking.QUICK_CHOOSE_NEW"
            :class="{
              'quick-action-btn': true,
              active: openPosterTypeDialog === PosterTypeDialogState.NEW,
            }"
            :picto="
              openPosterTypeDialog === PosterTypeDialogState.NEW
                ? 'mdi-chevron-double-down'
                : 'mdi-plus'
            "
            variant="primary"
          >
            {{ $t('poster.creation.menu.add_new') }}
          </pui-button>
          <pui-button
            id="quick-new-poster"
            :disabled="!lastBuiltType"
            @click="addPosterFromLastType"
            v-track:click="Tracking.QUICK_NEW"
            @mouseenter="setTempNewType(lastBuiltType)"
            @mouseleave="setTempNewType(null)"
            :outline="true"
            :title="piivoTranslate(lastBuiltType)"
            class="quick-action-btn"
            variant="primary"
            picto="mdi-clock-plus"
          >
            {{ lastBuiltType | piivoTranslate }}
          </pui-button>
        </pui-button-list>
      </template>
      <template #right>
        <p v-if="signageItems.length" class="pui-action-bar__action-subtitle">
          {{
            $tc('poster.creation.menu.demands_count', signageItems.length, {
              count: signageItems.length,
            })
          }}
        </p>
        <pui-button-list class="final-actions">
          <pui-button
            id="quick-btn-send"
            v-permission="Permissions.QUICK_SAVE"
            v-track:click="Tracking.QUICK_OPEN_SEND_CONFIRMATION"
            :flat="true"
            :disabled="disableSendButtons"
            @click="createPostersHook(CreationActions.CREATE)"
            class="pui-action-bar__button btn-send"
            variant="primary"
          >
            {{ $t('poster.creation.menu.create') }}
          </pui-button>
          <pui-button
            id="quick-btn-download"
            v-permission="Permissions.DOWNLOAD_PDF"
            v-track:click="Tracking.QUICK_OPEN_DOWNLOAD_CONFIRMATION"
            :flat="true"
            :disabled="disableSendButtons"
            @click="createPostersHook(CreationActions.DOWNLOAD)"
            class="pui-action-bar__button btn-download"
            variant="primary"
          >
            {{ $t('poster.creation.menu.download') }}
          </pui-button>
          <pui-button
            id="quick-btn-print-auto"
            v-permission="Permissions.PRINT_AUTO"
            v-track:click="Tracking.QUICK_OPEN_PRINT_CONFIRMATION"
            :flat="true"
            :disabled="disableSendButtons"
            @click="createPostersHook(CreationActions.PRINT_AUTO)"
            class="pui-action-bar__button btn-print"
            variant="primary"
          >
            {{ $t('poster.creation.menu.print_auto') }}
          </pui-button>
        </pui-button-list></template
      >
    </pui-action-bar>

    <div class="wrapper-column">
      <!-- Cart -->
      <transition name="expand-width">
        <div
          v-if="showCart && signageItems.length > 0"
          :class="{ 'creation-cart-wrapper': true, showBorder: !!openPosterTypeDialog }"
        >
          <poster-creation-cart
            ref="creationCart"
            :disabled="!!openPosterTypeDialog"
            :nbSignageItemsInvalid="nbSignageItemsInvalid"
            :signageItems="signageItems"
            :currentSignageItem="currentSignageItem"
            :selectedSignageItems="selectedSignageItems"
            :showTempPosterTypeZone="openPosterTypeDialog === PosterTypeDialogState.NEW"
            :isChangingCurrentType="openPosterTypeDialog === PosterTypeDialogState.EDIT"
            :tempPosterType="tempNewType"
            @onSignageItemClick="onSignageItemClick"
            @deleteSignageItems="deleteSignageItems"
            @cartMoveUp="moveUpInCart"
            @cartMoveDown="moveDownInCart"
          ></poster-creation-cart>
        </div>
      </transition>

      <!-- Signage edition (form) -->
      <pui-side-panel
        :open="attrsExpansion"
        v-if="signageItems.length > 0 && currentSignageItem != null"
        openWidth="1000px"
        closedWidth="400px"
        mode="width"
        hideButton
        class="primary-form-panel"
      >
        <pui-flex class="primary-form-panel-control" alignItems="center">
          <pui-button
            @click="attrsExpansion = !attrsExpansion"
            class="toggle-open-button"
            picto="mdi-arrow-top-right-bottom-left"
            flat
            variant="secondary"
          />
        </pui-flex>
        <pui-expand-height>
          <!-- wrapper div so transition doesnt cut poster-validation-alert padding -->
          <div v-if="!currentSignageItem.areAttributesValid">
            <poster-validation-alert>
              <p>{{ $t('poster.creation.form.invalid_form') }}</p>
            </poster-validation-alert>
          </div>
        </pui-expand-height>
        <pui-form-panel class="form-content scroll-body">
          <div class="poster-form-group step1">
            <div>
              <poster-validation-alert v-if="!allFamilies || allFamilies.length === 0">
                <p>{{ $t('poster.family_selector.no_families.message') }}</p>
              </poster-validation-alert>

              <pui-flex class="poster-type-button poster-type-button__root" alignItems="center">
                <i18n path="poster.type_button.type" class="poster-type-button__label">
                  <template #type>
                    {{ currentType ? piivoTranslate(currentType) : '' }}
                  </template>
                </i18n>

                <pui-button
                  @click="openEditPosterTypeDialog"
                  variant="priamry"
                  class="poster-type-button__btn"
                >
                  {{ $t('poster.type_button.change') }}
                </pui-button>
              </pui-flex>
            </div>
          </div>
          <div
            v-if="
              currentForm != null &&
                currentForm.inputGroups &&
                currentForm.inputGroups.length > 0 &&
                currentSignageItem
            "
            class="poster-form-group step2"
          >
            <!-- Entrant inputGroups (magasin / référence...) -->
            <pui-attribute-group-panel
              v-for="group in computedInputGroups"
              :key="group.itemId"
              :group="group"
            >
              <template v-if="group.attributes && currentSignageItem.values">
                <pui-attribute-panel
                  v-for="attribute in group.attributes"
                  :ref="`formAttribute_${attribute.alias}`"
                  :key="attribute.itemId"
                  :attribute="attribute"
                  :value="currentSignageItem.values[attribute.alias]"
                  :loadOptionsFunction="getPossibleValues"
                  @valueChanged="attributeValueChanged(attribute, $event, true)"
                  @searchRequested="openAttributeSearch($event, true)"
                  :importExternalFiles="importExternalFiles"
                  :forceExtendedLink="attrsExpansion ? { value: true } : null"
                />
              </template>
            </pui-attribute-group-panel>
          </div>
          <!--            Reste des attributs qui ne sont pas des entrants -->
          <div v-if="currentForm != null && currentForm.attributeGroups" class="poster-form-group">
            <pui-attribute-group-panel
              v-for="group in computedAttributeGroups"
              :key="group.itemId"
              :group="group"
            >
              <template v-if="group.attributes && currentSignageItem.values">
                <pui-attribute-panel
                  v-for="attribute in group.attributes"
                  :ref="`formAttribute_${attribute.alias}`"
                  :key="attribute.itemId"
                  :attribute="attribute"
                  :value="currentSignageItem.values[attribute.alias]"
                  :loadOptionsFunction="getPossibleValues"
                  @valueInput="updateAttributeValue(attribute.alias, $event)"
                  @valueChanged="attributeValueChanged(attribute, $event, false)"
                  @searchRequested="openAttributeSearch($event, false)"
                  :importExternalFiles="importExternalFiles"
                  :forceExtendedLink="attrsExpansion ? { value: true } : null"
                />
              </template>
            </pui-attribute-group-panel>
          </div>
        </pui-form-panel>
      </pui-side-panel>

      <!-- Template viewer -->
      <template v-if="signageItems.length > 0">
        <pui-flex
          v-if="fontsError"
          class="preview-fonts-error"
          flex="1"
          direction="column"
          alignItems="center"
        >
          <i class="mdi mdi-alert error-icon" />
          <i18n path="poster.creation.error.fonts.primary"></i18n>
          <i18n path="poster.creation.error.fonts.secondary">
            <template #clickToReload>
              <span @click="reloadPage" class="reload-button">{{
                $t('poster.creation.error.fonts.click_to_reload')
              }}</span>
            </template>
          </i18n>
        </pui-flex>

        <!-- Signage viewer -->
        <pui-document-viewer
          ref="viewer"
          v-else
          :template="currentTemplate"
          :bindings="currentSignageItem != null ? currentSignageItem.values : null"
          :types="valuesTypes"
        />
      </template>

      <!-- Signage formats with copies -->
      <div
        v-if="signageItems.length > 0 && currentSignageItem != null"
        class="panel-format beaSidePanel"
      >
        <pui-expand-height>
          <!-- wrapper div so transition doesnt cut poster-validation-alert padding -->
          <div v-if="!currentSignageItem.isFormatsCopiesValid">
            <poster-validation-alert>
              <p>
                {{ $t('poster.creation.formats_form.no_form_selected') }}
              </p>
            </poster-validation-alert>
          </div>
        </pui-expand-height>
        <div class="scroll-body">
          <div class="format-groups-wrapper">
            <div v-if="typeDataError" class="type-data-error">
              <h3 class="title">
                {{ $t('poster.creation.error.type_data.message.title') }}
              </h3>
              <pui-flex class="type-data-error-message" alignItems="center">
                <i class="mdi mdi-alert error-icon" />
                <pui-flex direction="column">
                  <i18n path="poster.creation.error.type_data.message.primary"></i18n>
                  <i18n path="poster.creation.error.type_data.message.secondary">
                    <template #clickToReload>
                      <span @click="reloadPage" class="reload-button">{{
                        $t('poster.creation.error.type_data.message.click_to_reload')
                      }}</span>
                    </template>
                  </i18n>
                </pui-flex>
              </pui-flex>
            </div>
            <div
              v-else
              v-for="formatGroup in formatGroups"
              :key="formatGroup.itemId"
              class="format-group pui-group-panel"
            >
              <h3 class="titre">
                {{ formatGroup | piivoTranslate }}
              </h3>
              <pui-input-number-picker
                v-for="format in formatGroup.formats"
                v-if="
                  currentSignageItem &&
                    currentSignageItem.formatsCopiesMap &&
                    currentSignageItem.formatsCopiesMap[format.itemId]
                "
                :id="`quick-input-copies-${format.itemId}`"
                :key="format.itemId"
                v-model="currentSignageItem.formatsCopiesMap[format.itemId].copies"
                :min="MIN_COPIES"
                :max="MAX_COPIES"
                :step="1"
                :class="{
                  actif: currentSignageItem.formatId === format.itemId,
                }"
                :label="piivoTranslate(format)"
                @focus="changeFormat(format.itemId, false)"
                @input="onCopiesChanged"
              />
            </div>
          </div>
        </div>
        <div class="form-action">
          <div class="comment-zone">
            <pui-input
              v-model="currentSignageItem.comment"
              :label="$t('poster.creation.formats_form.comment.label')"
              :textarea="true"
              :rows="3"
              type="text"
            >
              <pui-input-message :message="$t('poster.creation.formats_form.comment.message')" />
            </pui-input>
          </div>
        </div>
      </div>

      <!-- New poster type selector -->
      <pui-dialog
        ref="newPosterTypeDialog"
        :showHeader="false"
        :showCloseButton="false"
        :noBackground="true"
        position="absolute"
        transition="slide-up"
        class="new-poster-type-dialog"
        width="100%"
      >
        <poster-main-type-selector
          :singleLoader="true"
          @hoverType="setTempNewType"
          @buildingType="setTempNewType"
          @addedPoster="onNewPosterAdded"
          class="new-poster-type-selector"
        >
          <template #title>
            {{ $t('poster.creation.new.title') }}
          </template>
        </poster-main-type-selector>
      </pui-dialog>

      <!-- Edit poster type selector -->
      <pui-dialog
        ref="editPosterTypeDialog"
        :showHeader="false"
        :showCloseButton="false"
        :noBackground="true"
        position="absolute"
        transition="slide-up"
        class="edit-poster-type-dialog"
        width="100%"
      >
        <poster-type-gallery
          :typeThumbnailListeners="
            (_type, family) => ({
              click: () => onTypeChange(_type, family),
              mouseenter: () => setCurrentPosterTempType(_type),
              mouseleave: () => setCurrentPosterTempType(null),
            })
          "
          :typeThumbnailProps="
            _type => ({
              clickable: true,
              checked: currentType && currentType.itemId === _type.itemId,
            })
          "
          :initialFamilyId="currentFamily ? currentFamily.itemId : null"
        >
          <template #title>
            {{ $t('poster.creation.change_type.title') }}
          </template>

          <template #typeCenter="{ item: type, family }">
            <pui-thumbnail-main-action @click.stop="onTypeChange(type, family)">
              {{ $t('poster.creation.change_type.select_type') }}
            </pui-thumbnail-main-action>
          </template>
        </poster-type-gallery>
      </pui-dialog>
    </div>

    <!-- Send selection confirmation dialog -->
    <poster-send-selection-confirmation-dialog
      ref="sendSelectionConfirmationDialog"
      :nbSelectedSignageItems="selectedSignageItems.length"
      :nbSelectedSignageItemsInvalid="nbSelectedSignageItemsInvalid"
    ></poster-send-selection-confirmation-dialog>

    <!-- Send limited selection dialog -->
    <poster-send-limited-count-confirmation-dialog
      ref="sendLimitedCountConfirmationDialog"
      :nbSignageItems="selectedSignageItems.length || signageItems.length"
      :posterLimit="posterLimit"
      page="creation"
    ></poster-send-limited-count-confirmation-dialog>
  </div>
</template>
<script>
import RenderManager from 'piivo-poster-engine/src/lib/renderer/renderManager';
import RenderTools from 'piivo-poster-engine/src/lib/renderer/renderTools';
import Vue from 'vue';
import { mapGetters } from 'vuex';

import helpers from '../../../../core/helpers';
import services from '../../../../core/services';
import store from '../../../../core/store';
import { AttributeTypes } from '../../../common/constants';
import { areAllAttributesValid } from '../../../common/helpers/attributeValidators';
import { iterateForm, updateOn } from '../../../common/helpers/formsHelper';
import { CreationActions, MAX_COPIES, MIN_COPIES } from '../../constants';
import { getPosterLimit } from '../../helpers/posterHelper';
import ConstantsMixin from '../../mixins/ConstantsMixin';
import { getPosterService } from '../../services';
import {
  NAMESPACE as POSTER_NAMESPACE,
  SET_CURRENT_SIGNAGE_ITEM_ID,
  SET_LAST_BUILT_FAMILY,
  SET_LAST_BUILT_TYPE,
} from '../../store/modules/poster';
import AttributeManager from '../../tools/attributeManager';
import posterCreationCart from './CreationCart';
import posterMainTypeSelector from './MainTypeSelector.vue';

// Cart actions
const CartAction = {
  DELETE_SELECTION: 'DELETE_SELECTION',
  DELETE_ALL: 'DELETTE_ALL',
};

const PosterTypeDialogState = {
  NEW: 'new',
  EDIT: 'edit',
};

export default {
  name: 'poster-cart',
  components: {
    posterCreationCart,
    posterMainTypeSelector,
  },
  mixins: [ConstantsMixin],
  props: {
    /**
     * Flag to add first signage on init
     */
    addFirstSignage: {
      type: Boolean,
      default: false,
    },
    /**
     * Retrieves attribute options values for the current template/signage item
     */
    getPossibleValues: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      // Constants
      MIN_COPIES,
      MAX_COPIES,

      // Main datas
      currentMessage: '',
      CreationActions,
      CartAction,
      showCart: false,
      attrsExpansion: false,
      currentSignageItem: null,
      selectedSignageItems: [],
      currentCartAction: null,
      allFamilies: [],
      /**
       * The family selected in the family selector
       */
      currentFamily: null,
      /**
       * The type selected in the type selector
       */
      currentType: null,
      /**
       * The selected poster's type data.
       * Not exactly synced with 'currentType'
       */
      selectedPosterTypeData: null,
      currentTemplate: null,
      currentSearchParams: null,

      /**
       * If there was an error retrieving type data:
       * form/formats/templates/fonts...
       */
      typeDataError: false,
      /**
       * If there was an error retrieving
       * fonts of a template
       */
      fontsError: false,

      // Spinner datas
      cptSpinner: 0,
      isPerformingRequest: false,
      importExternalFileCount: 0,

      // Cart selection
      selectedSignageIndex: 0,

      // Search datas
      selectedSearchMode: null,
      /**
       * Single search result: the selected item from the search results
       * Multi search result: the items in the search summary table
       */
      selectedSearchedItems: [],

      openPosterTypeDialog: false,
      PosterTypeDialogState,
      tempNewType: null,
    };
  },
  computed: {
    /**
     * Gets or sets the signage items in the store
     */
    signageItems: {
      get() {
        return getPosterService('cartState').get();
      },
      set(nextSignageItems) {
        getPosterService('cartState').set(nextSignageItems);
      },
    },
    /**
     * Gets or sets the last built poster type in the store
     */
    lastBuiltFamily: {
      get() {
        return store.getters[`${POSTER_NAMESPACE}/lastBuiltFamily`];
      },
      set(nextFamily) {
        store.commit(`${POSTER_NAMESPACE}/${SET_LAST_BUILT_FAMILY}`, nextFamily);
      },
    },
    /**
     * Gets or sets the last built poster type in the store
     */
    lastBuiltType: {
      get() {
        return store.getters[`${POSTER_NAMESPACE}/lastBuiltType`];
      },
      set(nextType) {
        store.commit(`${POSTER_NAMESPACE}/${SET_LAST_BUILT_TYPE}`, nextType);
      },
    },
    ...mapGetters({
      currentSignageItemId: `${POSTER_NAMESPACE}/currentSignageItemId`,
      user: 'user',
    }),
    /**
     * Compute attribute groups in order to evaluate expression
     */
    computedInputGroups() {
      if (
        this.selectedPosterTypeData &&
        this.currentSignageItem &&
        this.currentSignageItem.values &&
        this.selectedPosterTypeData &&
        this.selectedPosterTypeData.valuesTypes
      ) {
        const deepInputGroups = Object.assign({}, this.currentForm.inputGroups);
        return AttributeManager.executeComputeMethod(
          deepInputGroups,
          this.currentSignageItem.values,
          this.selectedPosterTypeData.valuesTypes
        );
      }
      return [];
    },
    /**
     * Compute attribute groups in order to evaluate expression
     */
    computedAttributeGroups() {
      if (
        this.selectedPosterTypeData &&
        this.currentSignageItem &&
        this.currentSignageItem.values &&
        this.selectedPosterTypeData &&
        this.selectedPosterTypeData.valuesTypes
      ) {
        // Create deep copy
        const deepAttributeGroups = Object.assign({}, this.currentForm.attributeGroups);
        return AttributeManager.executeComputeMethod(
          deepAttributeGroups,
          this.currentSignageItem.values,
          this.selectedPosterTypeData.valuesTypes
        );
      }
      return [];
    },
    currentForm() {
      return this.selectedPosterTypeData != null ? this.selectedPosterTypeData.form : null;
    },
    valuesTypes() {
      return this.selectedPosterTypeData != null && this.selectedPosterTypeData.valuesTypes
        ? this.selectedPosterTypeData.valuesTypes
        : null;
    },
    /**
     * @returns {Map<string, object>} - attributeMap of the current poster typeData
     */
    attributeMap() {
      return this.selectedPosterTypeData != null && this.selectedPosterTypeData.attributeMap
        ? this.selectedPosterTypeData.attributeMap
        : null;
    },
    formatGroups() {
      return this.selectedPosterTypeData != null ? this.selectedPosterTypeData.formatGroups : null;
    },
    /**
     * @returns {string} the selected type's alias
     */
    selectedTypeAlias() {
      return this.selectedPosterTypeData && this.selectedPosterTypeData.type
        ? this.selectedPosterTypeData.type.alias
        : null;
    },
    /**
     * @returns {number} the number of invalid items in the cart
     */
    nbSignageItemsInvalid() {
      return this.signageItems.filter(s => s.isValid === false).length;
    },
    /**
     * @returns {number} the number of **selected** items in the cart that are invalid
     */
    nbSelectedSignageItemsInvalid() {
      return this.selectedSignageItems.filter(s => s.isValid === false).length;
    },
    /**
     * @returns {boolean} if send buttons should be disabled
     */
    disableSendButtons() {
      return (
        !this.signageItems.length ||
        this.fontsError ||
        this.typeDataError ||
        (!this.selectedSignageItems.length && this.nbSignageItemsInvalid > 0)
      );
    },
    /**
     * @returns the max number of posters that can be sent
     */
    posterLimit() {
      return getPosterLimit();
    },
  },
  watch: {
    currentSignageItem(nextValue, prevValue) {
      // Commit new current signage item
      store.commit(
        `${POSTER_NAMESPACE}/${SET_CURRENT_SIGNAGE_ITEM_ID}`,
        nextValue ? nextValue.id : null
      );

      if (nextValue !== prevValue) {
        this.$emit('changeSignageItem', nextValue);
      }
    },
    currentForm(nextValue, prevValue) {
      if (nextValue !== prevValue) {
        this.$emit('changeForm', nextValue);
      }
    },
    currentTemplate(nextValue, prevValue) {
      if (nextValue !== prevValue) {
        this.$emit('changeTemplate', nextValue);
      }
    },
    signageItems(nextCartValue) {
      // Update our selection to only preserve the items that are still in the cart
      this.selectedSignageItems = this.selectedSignageItems.filter(selectedPoster =>
        nextCartValue.find(poster => poster.id === selectedPoster.id)
      );
    },
  },
  methods: {
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    piivoTranslateLabel(value) {
      return Vue.filter('piivoTranslateLabel')(value);
    },
    /**
     * Start global loading (global spinner).
     * @param message - Loading message
     */
    startLoading(message) {
      this.cptSpinner++;
      this.currentMessage = message;
      this.isPerformingRequest = this.cptSpinner !== 0;
    },
    /**
     * Stop global loading.
     */
    stopLoading() {
      if (this.cptSpinner > 0) {
        this.cptSpinner--;
      }
      this.isPerformingRequest = this.cptSpinner !== 0;
    },
    /**
     * Reloads the page to recover from a type data (form, formats) or fonts error
     */
    reloadPage() {
      this.$router.go();
    },
    // CART
    /**
     * Method execute on key up UP in cart dialog to navigate into items
     * @returns {void}
     */
    async moveUpInCart() {
      if (this.selectedSignageIndex === 0) {
        return;
      }
      this.selectedSignageIndex--;
      const currentSelectedSignageItem = this.signageItems[this.selectedSignageIndex];
      await this.$nextTick();
      this.setCurrentSignageItem(currentSelectedSignageItem, false);
      this.$refs.creationCart.focusItem(this.selectedSignageIndex);
    },
    /**
     * Method execute on key up DOWN in cart dialog to navigate into items
     * @returns {void}
     */
    async moveDownInCart() {
      if (this.selectedSignageIndex >= this.signageItems.length - 1) {
        return;
      }
      this.selectedSignageIndex++;

      // Click without SHIFT or CTRL key => reset selection with clicked signage item (current)
      const currentSelectedSignageItem = this.signageItems[this.selectedSignageIndex];
      await this.$nextTick();
      this.setCurrentSignageItem(currentSelectedSignageItem, false);
      this.$refs.creationCart.focusItem(this.selectedSignageIndex);
    },
    /**
     * Show/Hide signage items list (toggle cart)
     */
    toggleCart() {
      this.showCart = !this.showCart;

      this.$nextTick(() => {
        if (this.signageItems.length && this.showCart) {
          this.$refs.creationCart.focusItem(this.selectedSignageIndex);
          // Click without SHIFT or CTRL key => reset selection with clicked signage item (current)
          const currentSelectedSignageItem = this.signageItems[this.selectedSignageIndex];
          this.setCurrentSignageItem(currentSelectedSignageItem, false);
        }
      });
    },
    /**
     * Open the cart confirmation dialog.
     * @param {String} currentCartAction - Define the current cart action
     */
    openCartConfirmationDialog(currentCartAction) {
      this.currentCartAction = currentCartAction;
      if (this.$refs.cartConfirmDialog != null) {
        this.$refs.cartConfirmDialog.open();
      }
    },
    /**
     * Close the cart confirmation dialog
     */
    closeCartConfirmationDialog() {
      if (this.$refs.cartConfirmDialog != null) {
        this.$refs.cartConfirmDialog.close();
      }

      this.currentCartAction = null;
    },
    /**
     * Execute cart actions.
     */
    executeCartAction() {
      if (this.currentCartAction === CartAction.DELETE_SELECTION) {
        this.deleteSelectedSignageItems();
      } else if (this.currentCartAction === CartAction.DELETE_ALL) {
        this.deleteAllSignageItems();
      }

      this.closeCartConfirmationDialog();
    },
    /**
     * Launchs on signage item click
     * Manage manage current signage item and signage items selection (with CTRL and SHIFT shortcuts).
     * @param {Object} signageItem - Signage item clicked
     * @param {Object} clickEvent - Click event object
     * @param {boolean} wasCheckbox - If the click came from the item's checkbox
     */
    onSignageItemClick(signageItem, clickEvent, wasCheckbox) {
      const indexClicked = this.signageItems.findIndex(signage => signage === signageItem);

      if (clickEvent && (wasCheckbox || clickEvent.shiftKey || clickEvent.ctrlKey)) {
        if (clickEvent.shiftKey) {
          // Click on signage item with SHIFT key
          const indexClicked = this.signageItems.indexOf(signageItem);
          const currentIndex = this.signageItems.indexOf(this.currentSignageItem);

          // Change selection (except if there is nothing to change in selection)
          if (currentIndex !== indexClicked || this.selectedSignageItems.length > 1) {
            // Set range signage items selection
            this.selectedSignageItems = this.signageItems.slice(
              Math.min(indexClicked, currentIndex),
              Math.max(indexClicked, currentIndex) + 1
            );
            if (!wasCheckbox) {
              this.setCurrentSignageItem(signageItem, false);
              this.selectedSignageIndex = indexClicked;
            }
          }
        } else if (wasCheckbox || clickEvent.ctrlKey) {
          // Click on signage item with CTRL key
          const indexInSelection = this.selectedSignageItems.indexOf(signageItem);
          if (indexInSelection !== -1) {
            // Delete signage item from selection
            this.selectedSignageItems.splice(indexInSelection, 1);
          } else {
            // Add signage item to selection
            this.selectedSignageItems.push(signageItem);
            if (!wasCheckbox) {
              // Change current signage item (only if the click wasn't from the checkbox)
              this.setCurrentSignageItem(signageItem, false);
              this.selectedSignageIndex = indexClicked;
            }
          }
        }
      } else {
        // Click without SHIFT or CTRL key => reset selection with clicked signage item (current)
        if (signageItem !== this.currentSignageItem) {
          this.setCurrentSignageItem(signageItem, false);
          this.selectedSignageIndex = indexClicked;
        }
      }
    },
    /**
     * Change current signage item.
     * @param {Object} signageItem - New current signage item
     */
    async setCurrentSignageItem(signageItem, focusFirstAttribute = true) {
      if (signageItem !== this.currentSignageItem) {
        if (this.currentSignageItem) {
          this.currentSignageItem.unread = false;
        }

        // Check current signage item before select the new signage item
        this.checkSignageItem(this.currentSignageItem);

        // Init current signage and screen with signage item type and informations (no signage item update)
        const type = signageItem.type;

        this.currentSignageItem = signageItem;
        const typeData = await this.getTypeData(type);

        this.init(typeData, false, focusFirstAttribute);

        // Set family and type
        await this.setCurrentFamilyAndType(
          this.currentSignageItem.familyId,
          type != null ? type.alias : null
        );
      }
    },
    /**
     * Clear all selected signage items.
     */
    deleteSelectedSignageItems() {
      this.deleteSignageItems(this.selectedSignageItems);
    },
    /**
     * Clear all signage items.
     */
    deleteAllSignageItems() {
      this.signageItems = [];
      this.currentSignageItem = null;
      this.selectedSignageItems = [];
    },
    /**
     * Delete the desired signage items (object in parameter).
     * @param {Array} signageItemsToDelete - Array of signage items to delete
     */
    deleteSignageItems(signageItemsToDelete) {
      let newCurrentSignageItem = null;
      if (signageItemsToDelete.includes(this.currentSignageItem)) {
        const currentIndex = this.signageItems.indexOf(this.currentSignageItem);
        // Search for new current item after current signage item
        for (let index = currentIndex + 1; index < this.signageItems.length; index++) {
          // 1. If there are fewer deleted items than selected items => search in other selected items
          // 2. If there are as many deleted items as selected items => search outside deleted items
          const element = this.signageItems[index];
          if (
            (signageItemsToDelete.length < this.selectedSignageItems.length &&
              this.selectedSignageItems.includes(element)) ||
            (signageItemsToDelete.length === this.selectedSignageItems.length &&
              signageItemsToDelete.includes(element) === false)
          ) {
            newCurrentSignageItem = element;
            break;
          }
        }

        if (newCurrentSignageItem == null) {
          // Search for new current item before current signage item
          for (let index = currentIndex - 1; index >= 0; index--) {
            const element = this.signageItems[index];
            // 1. If there are fewer deleted items than selected items => search in other selected items
            // 2. If there are as many deleted items as selected items => search outside deleted items
            if (
              (signageItemsToDelete.length < this.selectedSignageItems.length &&
                this.selectedSignageItems.includes(element)) ||
              (signageItemsToDelete.length === this.selectedSignageItems.length &&
                signageItemsToDelete.includes(element) === false)
            ) {
              newCurrentSignageItem = element;
              break;
            }
          }
        }
      } else {
        // No change to current items
        newCurrentSignageItem = this.currentSignageItem;
      }

      // Delete signage items (copy from array because of changes made on it)
      const signageItemsCopy = signageItemsToDelete.slice(0);
      for (const signageItem of signageItemsCopy) {
        const indexToDelete = this.signageItems.indexOf(signageItem);
        if (indexToDelete !== -1) {
          // Update signage items selection
          const indexInSelection = this.selectedSignageItems.indexOf(signageItem);
          if (indexInSelection !== -1) {
            this.selectedSignageItems.splice(indexInSelection, 1);
          }

          // Delete signage item
          this.signageItems.splice(indexToDelete, 1);
        }
      }

      // Change new current signage item if necessary
      if (newCurrentSignageItem != null) {
        if (newCurrentSignageItem !== this.currentSignageItem) {
          this.setCurrentSignageItem(newCurrentSignageItem);
        }
      } else {
        this.currentSignageItem = null;
        this.selectedSignageItems = [];
      }
    },
    /**
     * Get type data (load it if not already loaded in the types map).
     * @param {object} type - type object
     * @returns Promise to get type data
     */
    async getTypeData(type) {
      if (!type) {
        return null;
      }

      try {
        const existingTypeData = getPosterService('templates').getTypeData(type.itemId);
        if (existingTypeData) {
          return existingTypeData;
        } else {
          this.startLoading();
          const typeData = await getPosterService('templates').loadTypeData(type, null);
          this.stopLoading();
          return typeData;
        }
      } catch (err) {
        this.fontsError = this.fontsError || err.fontsError;
        this.typeDataError = true;

        // Error
        this.stopLoading();
        services.getService('alerts').alertError(this.$t('poster.creation.error.type_data.alert'));
        return null;
      }
    },
    // ADD NEW POSTER
    /**
     * Toggles the new poster dialog
     */
    async toggleNewPosterDialog() {
      if (this.openPosterTypeDialog === PosterTypeDialogState.NEW) {
        this.closeNewPosterDialog();
      } else {
        this.closeEditPosterTypeDialog();
        this.showCart = true;
        await this.$nextTick();
        if (this.$refs.newPosterTypeDialog) {
          this.$refs.newPosterTypeDialog.open();
          this.openPosterTypeDialog = PosterTypeDialogState.NEW;
          await this.$nextTick();
          if (this.$refs.creationCart) {
            this.$refs.creationCart.focusTempPoster();
          }
        }
      }
    },
    /**
     * Closes the new poster dialog
     */
    closeNewPosterDialog() {
      if (this.$refs.newPosterTypeDialog) {
        this.$refs.newPosterTypeDialog.close();
      }
      this.openPosterTypeDialog = false;
    },
    /**
     * Sets the temporary new poster type
     *
     * @param {object} type - the temporary type
     */
    async setTempNewType(type) {
      this.tempNewType = type;
      if (type) {
        await this.$nextTick();
        if (this.$refs.creationCart) {
          this.$refs.creationCart.focusTempPoster();
        }
      }
    },
    /**
     * Called when a new poster is added
     *
     * @param {object} poster - the new poster
     */
    async onNewPosterAdded(poster) {
      this.setTempNewType(null);

      // Focus the cart item before setting the current signage item
      // so we don't remove focus from the first attribute
      await this.$nextTick();
      if (this.$refs.creationCart) {
        this.$refs.creationCart.focusItem(0);
      }

      this.setCurrentSignageItem(poster, true);
      this.closeNewPosterDialog();
    },
    /**
     * Adds a poster to the cart with the last type
     * that has been added
     */
    async addPosterFromLastType() {
      const poster = await getPosterService('signages').buildCartPoster(
        this.lastBuiltFamily ? this.lastBuiltFamily.itemId : null,
        this.lastBuiltType.itemId,
        this.user.extendedProperties
      );

      // Add poster to start of cart
      this.signageItems.unshift(poster); // Updates the store via computed setter

      // Focus the cart item before setting the current signage item
      // so we don't remove focus from the first attribute
      await this.$nextTick();
      if (this.$refs.creationCart) {
        this.$refs.creationCart.focusItem(0);
      }

      this.setCurrentSignageItem(poster, true);
    },
    // FAMILY / TYPE
    /**
     * Opens the edit poster type dialog
     */
    async openEditPosterTypeDialog() {
      this.showCart = true;
      await this.$nextTick();
      if (this.$refs.editPosterTypeDialog) {
        this.$refs.editPosterTypeDialog.open();
        this.openPosterTypeDialog = PosterTypeDialogState.EDIT;
        await this.$nextTick();
        if (this.$refs.creationCart) {
          this.$refs.creationCart.focusTempPoster();
        }
      }
    },
    /**
     * Closes the edit poster type dialog
     */
    closeEditPosterTypeDialog() {
      if (this.$refs.editPosterTypeDialog) {
        this.$refs.editPosterTypeDialog.close();
      }
      // Do not reset openPosterTypeDialog: the new type dialog
      // can be opened at the same time
    },
    /**
     * Sets the temporary edit type on the current poster
     *
     * @param {object} type - the hoverd type
     */
    setCurrentPosterTempType(type) {
      this.$set(this.currentSignageItem, '_tempType', type);
    },
    /**
     * Triggered when the selected type is changed (with potentially a new family)
     *
     * @param {object} family - the new type
     * @param {object} family - the family the type belongs to
     */
    onTypeChange(type, family) {
      this.closeEditPosterTypeDialog();
      this.openPosterTypeDialog = false;
      this.setCurrentPosterTempType(null);

      if (this.currentType && this.currentType.itemId === type.itemId) {
        return;
      }

      if (family.itemId !== this.currentFamily.itemId) {
        this.currentFamily = family;
      }
      this.currentType = type;

      // Update our current signage item's type once type/family are updated
      this.updateCurrentSignageType(this.currentType);
    },
    /**
     * Sets the current family according to the given id
     * Sets the current type according to the given alias
     * If the type alias is not given, it will set the current type
     * to be the default family's type
     *
     * @param {string} familyId - the new family id
     * @param {string} [typeAlias] - the new type alias. Leave null to autoselect
     * the family's default type
     */
    async setCurrentFamilyAndType(familyId, typeAlias = null) {
      this.currentFamily = this.allFamilies.find(f => f.itemId === familyId);

      if (typeAlias) {
        await this.setTypeByAlias(typeAlias);
      } else {
        const currentFamilyRootTypes = await this.getCurrentFamilyRootTypes();
        if (currentFamilyRootTypes.length) {
          // Use default type if we could not find the type from "typeAlias"
          this.currentType = getPosterService('templates').getDefaultTemplateType(
            currentFamilyRootTypes
          );
        }
      }
    },
    /**
     * Updates the current type according to the given alias
     *
     * @param {string} typeAlias - the type alias
     */
    async setTypeByAlias(typeAlias) {
      const currentFamilyRootTypes = await this.getCurrentFamilyRootTypes();
      const newType = getPosterService('templates').getTemplateTypeByAlias(
        currentFamilyRootTypes,
        typeAlias
      );

      this.currentType = newType;
    },
    /**
     * Loads the root types for the current family. The result is cached.
     *
     * @returns {Promise<object[]>} the root types of the family
     */
    async getCurrentFamilyRootTypes() {
      if (!this.currentFamily) {
        return null;
      }
      const familyRootTypes = await getPosterService('templates').loadFamilyRootTypes(
        this.currentFamily.itemId
      );
      return familyRootTypes;
    },
    // CURRENT POSTER
    /**
     * Type changed in type selector.
     * @param {Object} type - New type
     */
    async updateCurrentSignageType(type) {
      // Get type with data
      const typeData = await this.getTypeData(type);
      this.init(typeData);

      // Call to getValues with the new type/template
      return this.getValues(false);
    },
    /**
     * Initialize screen for the type in parameter.
     * @param {Object} typeData - Type to use for initialization
     * @param {Boolean} updateCurrentSignage - Init or update current signage item (copies, values etc.)
     */
    init(typeData = null, updateCurrentSignage = true, focusFirstAttribute = true) {
      this.selectedPosterTypeData = typeData;
      if (this.selectedPosterTypeData != null && this.currentForm && this.formatGroups) {
        // Update signage item for the type (if flag is true)
        if (updateCurrentSignage) {
          getPosterService('signages').updateSignageItemForType(
            this.currentSignageItem,
            this.selectedPosterTypeData,
            (poster, alias, value, attribute) => {
              this.setCurrentPosterValue(alias, value, attribute, false, 'replace');
            }
          );
        }

        // Init formats copies validity
        this.onCopiesChanged();

        // Init template and selected format
        this.changeFormat(this.currentSignageItem.formatId, true);

        if (focusFirstAttribute) {
          // Focus first form attribute
          this.$nextTick(() => {
            let focusDone = false;
            const focusCb = attribute => {
              if (focusDone === false) {
                // Do not focus invisible attributes
                // or Links type attribute or Date(+time): don't want to open poppers
                const attributePanel = this.$refs[`formAttribute_${attribute.alias}`];
                if (
                  attributePanel != null &&
                  attributePanel[0] != null &&
                  attributePanel[0].isVisible &&
                  attribute.type !== AttributeTypes.LINKS &&
                  !(
                    attribute.type === AttributeTypes.DATE &&
                    attribute.options &&
                    attribute.options.showTime
                  )
                ) {
                  attributePanel[0].focus();
                  focusDone = true;
                }
              }
            };

            iterateForm(this.currentForm, focusCb);
          });
        }
      } else {
        // Empty init
        this.currentTemplate = null;
        if (updateCurrentSignage) {
          this.currentSignageItem.type = this.selectedPosterTypeData
            ? this.selectedPosterTypeData.type
            : null;
          this.currentSignageItem.formatsCopiesMap = {};
          this.currentSignageItem.inputsSummary = [];
        }
      }
    },
    /**
     * Change format and update template content ONLY
     * if the format is different from the current format
     * and `force: false`
     *
     * @param {String} format - New format id (Guid)
     * @param {boolean} force - if should update template
     * even if the format is the same as the current
     */
    changeFormat(formatId, force = false) {
      if (!force && this.currentSignageItem.formatId === formatId) {
        return;
      }

      RenderTools.clearCaches();
      this.currentSignageItem.formatId = formatId;
      if (formatId != null) {
        const template = this.selectedPosterTypeData.templates.get(formatId);
        this.currentTemplate = template != null ? template : null;
        if (this.currentTemplate) {
          this.onTemplateChanged();
        }
        RenderManager.searchNodeToInvalidate(
          this.currentTemplate,
          this.currentSignageItem.values,
          this.currentSignageItem.types,
          null,
          true
        );
      }
    },
    /**
     * Called when the template is changed (null template excluded)
     */
    onTemplateChanged() {
      const actionCb = cbAttr => {
        if (cbAttr.type === AttributeTypes.LINKS) {
          // Update links options, since their options depend on the template
          this.$nextTick(() => {
            const attributePanel = this.$refs[`formAttribute_${cbAttr.alias}`];
            if (attributePanel != null && attributePanel[0] != null) {
              attributePanel[0].loadOptions().catch(() => null);
            }
          });
        }
      };

      iterateForm(this.currentForm, actionCb);
    },
    /**
     * Check a signage item validity.
     * @param {Object} signageItem - The signage item to check
     */
    checkSignageItem(signageItem) {
      if (signageItem != null) {
        // Check if at least one of the formats copies number is filled
        const isCopiesValid = Object.keys(signageItem.formatsCopiesMap || {}).some(formatId =>
          getPosterService('signages').checkFormatCopies(signageItem.formatsCopiesMap, formatId)
        );

        let areAttributesValid = true;
        if (
          signageItem.type != null &&
          !!getPosterService('templates').getTypeData(signageItem.type.itemId)
        ) {
          const typeData = getPosterService('templates').getTypeData(signageItem.type.itemId);
          const form = typeData.form;

          const checkResult = areAllAttributesValid(signageItem.values, form, {
            validateInvisibleItems: false,
            breakOnFirstError: true,
          });
          const { errors } = checkResult;
          areAttributesValid = Object.keys(errors).length === 0;
        }

        signageItem.areAttributesValid = areAttributesValid;
        signageItem.isFormatsCopiesValid = isCopiesValid;
        signageItem.isValid = isCopiesValid && areAttributesValid;
      }
    },
    /**
     * Triggered when the copies of the current poster
     * changed. Checks the validity of the poster
     */
    onCopiesChanged() {
      // Don't check in real time if signage item is valid
      if (this.currentSignageItem.isValid === false) {
        this.checkSignageItem(this.currentSignageItem);
      }
    },
    /**
     * Update attributes value.
     * @param {String} attributeAlias - Attribute alias
     * @param {Object} newValue - New attribute value
     */
    updateAttributeValue(attributeAlias, newValue) {
      RenderTools.clearCaches();
      this.setCurrentPosterValue(attributeAlias, newValue, null, true, 'replace');
    },
    /**
     * Launches the "updateOn" mechanism for the attribute
     *
     * @param {object} attribute - the attribute whose updateOn to trigger
     */
    startUpdateOn(attribute) {
      const actionCb = cbAttr => {
        if (cbAttr.type === AttributeTypes.LINKS) {
          this.$nextTick(() => {
            const attributePanel = this.$refs[`formAttribute_${cbAttr.alias}`];
            if (attributePanel != null && attributePanel[0] != null) {
              attributePanel[0].loadOptions().catch(() => null);
            }
          });
        }
      };

      updateOn(this.currentForm, attribute, actionCb);
    },
    /**
     * Iterates on the inputGroups and attributeGroups of the current
     * form and executes the callback for the attributes whose aliases
     * match the given alias
     *
     * @param {string} alias - the alias to search for
     * @param {(attribute: object) => void} cb - receives the form attribute object
     */
    doFormAttrCb(alias, cb) {
      iterateForm(this.currentForm, attribute => {
        if (attribute.alias === alias) {
          cb(attribute);
        }
      });
    },
    /**
     * Update on attributes value changed.
     * @param {object} attribute - the changed attribute
     * @param {Object} newValue - New attribute value
     * @param {Boolean} isInput - Flag indicates if atttribute is a form input or not
     */
    attributeValueChanged(attribute, newValue, isInput) {
      this.updateAttributeValue(attribute.alias, newValue);

      this.startUpdateOn(attribute);

      if (isInput) {
        // Update inputs summary
        getPosterService('signages').updateInputsSummary(this.currentSignageItem);

        // Get values for inputs
        this.getValues(true, true).then(() => {
          this.checkSignageItem(this.currentSignageItem);
        });
      } else {
        this.checkSignageItem(this.currentSignageItem);
      }
    },
    /**
     * Imports external files to the attribute's storage
     *
     * @param {object} attribute - the attribute onto which we are uploading the files
     * @param {object[]} tempFiles - array of objects containing the file blobs to upload
     * @param {(tempFile, importedFile) => void} onFileUploadedCb - callback for when a file is finished uploading.
     * Receives the original temp file, and the uploaded file
     * @param {(cancelCb: (tempId: string) => void) => void} provideAbortCb - callback
     * that proves the cancel function
     */
    async importExternalFiles(attribute, tempFiles, onFileUploadedCb, provideAbortCb) {
      this.importExternalFileCount++;

      const importedFiles = await getPosterService('posterAttributes').importExternalFiles(
        attribute,
        tempFiles,
        onFileUploadedCb,
        provideAbortCb
      );

      this.importExternalFileCount--;
      return importedFiles;
    },
    /**
     * Update a value for the alias in parameter.
     * @param {String} alias - Object key (property name)
     * @param {Object} value - New value
     * @param {Object|null} attribute - the attribute for the alias
     * @param {Boolean} tainted - Tainted flag
     * @param {string} updateMode - how to update the value
     */
    setCurrentPosterValue(alias, value, attribute, tainted, updateMode) {
      if (!this.currentSignageItem) {
        return;
      }

      getPosterService('signages').setPosterTaintedValue(this.currentSignageItem, alias, tainted);
      getPosterService('signages').setPosterValue(
        this.currentSignageItem,
        alias,
        value,
        attribute,
        this.selectedPosterTypeData,
        updateMode
      );
      // When a value changed : search node in template to invalidate (refresh rendering)
      if (this.currentTemplate != null) {
        RenderManager.searchNodeToInvalidate(
          this.currentTemplate,
          this.currentSignageItem.values,
          this.currentSignageItem.types,
          alias,
          false
        );
      }
    },
    /**
     * Update tainted values.
     * @param {Object} taintedValues - Object with tainted values flags
     */
    setCurrentPosterTaintedValues(taintedValues) {
      if (!this.currentSignageItem) {
        return;
      }

      getPosterService('signages').mergePosterTaintedValues(this.currentSignageItem, taintedValues);
    },
    /**
     * Get values with template and input values (load bindings).
     * @param {Boolean} autoType (optional -> default true) - Set type automatically
     * @param {Boolean} resetValues (optional -> default false) - Reset all values (overwrite tainted values)
     */
    async getValues(autoType = true, resetValues = false) {
      if (this.currentSignageItem && this.currentForm && this.currentForm.inputGroups) {
        // Checks inputs before launching values call
        const checkResult = areAllAttributesValid(
          this.currentSignageItem.values,
          { inputGroups: this.currentForm.inputGroups },
          {
            validateInvisibleItems: false,
            breakOnFirstError: true,
          }
        );
        if (Object.keys(checkResult.errors).length > 0) {
          return Promise.resolve();
        }

        // Get values
        this.startLoading();
        try {
          const typeData = await this.getTypeData(this.currentSignageItem.type);

          const bindings = await getPosterService('values').loadBindings(
            this.currentTemplate != null ? this.currentTemplate.templateId : null,
            typeData,
            this.currentSignageItem,
            resetValues
          );

          // Success => clear alerts and stop loading
          services.getService('alerts').clearAlerts();
          this.stopLoading();

          // Set signage values
          for (const alias in bindings.values) {
            this.setCurrentPosterValue(
              alias,
              bindings.values[alias],
              null,
              this.currentSignageItem.taintedValues[alias],
              'replace'
            );
          }
          this.setCurrentPosterTaintedValues(bindings.taintedValues);

          for (const alias in bindings.values) {
            this.doFormAttrCb(alias, attribute => {
              this.startUpdateOn(attribute);
            });
          }

          // Set new type
          const oldTypeAlias = this.selectedTypeAlias;
          if (autoType && bindings && bindings.type !== oldTypeAlias) {
            await this.setTypeByAlias(bindings.type);
            this.updateCurrentSignageType(this.currentType);
          }

          // Retrieve current type data again (in case it changed above)
          const nextTypeData = await this.getTypeData(this.currentSignageItem.type);
          // Apply new formatsCopiesMap
          this.currentSignageItem.formatsCopiesMap = getPosterService(
            'signages'
          ).formatInfoToCopiesMap(nextTypeData.formatGroups, bindings.formats);
        } catch (err) {
          services
            .getService('alerts')
            .alertWarning(this.$t('poster.creation.warning.no_results_found'));
          this.stopLoading();
        }
      }
    },
    /**
     * Open search panel for the attribute in parameter.
     * @param {Object} attribute - The search attribute
     * @param {Boolean} isInput - Flag  that defines if attribute is an input
     */
    async openAttributeSearch(attribute, isInput = false) {
      // Init search panel and open it
      if (!attribute) {
        this.currentSearchParams = null;
        return;
      }

      this.currentSearchParams = {
        searchKey: attribute.alias,
        searchDefinition: attribute.search,
        isInput: isInput,
        valueField: attribute.options.valueField || attribute.alias,
      };

      // Compute summary columns
      if (
        this.currentSearchParams.searchDefinition &&
        this.currentSearchParams.searchDefinition.summary
      ) {
        const defaultFormData = await getPosterService('templates').getDefaultFormData();
        helpers
          .getHelper('tableHelper')
          .computeTableColumns(
            this.currentSearchParams.searchDefinition.summary.columns,
            defaultFormData != null ? defaultFormData.attributeMap : null
          );
      }
      // Open search panel
      this.$nextTick(() => {
        this.$refs.searchDialog.open();
      });
    },
    /**
     * Launch a search with the search value in parameter.
     * @param {Object} searchValues - Object with current search values (key: alias, value: search value)
     * @param {object} pagination - object with pagination parameters
     * @param {object} pagination.index - page index to request
     * @param {object} pagination.size - page size
     * @param {[sortKey: string, sortDirection: string][]} sorts - sorts entries
     */
    async search(searchValues, pagination, sorts) {
      if (
        !this.currentTemplate ||
        !this.currentTemplate.templateId ||
        !this.selectedSearchMode ||
        !this.selectedSearchMode.search ||
        !this.selectedSearchMode.search.searchSourceId
      ) {
        return [];
      }

      // Search with the values of:
      const parameters = [
        // The inputGroups of the poster form
        {
          attrGroups: this.currentForm != null ? this.currentForm.inputGroups : null,
          values: this.currentSignageItem.values,
        },
        // The attributeGroups of the search form
        {
          attrGroups: this.selectedSearchMode.search.searchForm
            ? this.selectedSearchMode.search.searchForm.attributeGroups
            : [],
          values: searchValues,
        },
      ];

      const results = await getPosterService('values').search(
        this.currentTemplate.templateId,
        this.selectedSearchMode.search.searchSourceId,
        parameters,
        pagination
      );

      return results;
    },
    /**
     * Close the search dialog.
     */
    closeSearchDialog() {
      if (this.$refs.searchDialog != null) {
        this.$refs.searchDialog.close();
      }

      this.selectedSearchedItems = [];
    },
    /**
     * Update selected searched items in searchPanel component.
     * @param {Array} selectedSearchItems: array contains selected items in searchPanel
     * @param {Boolean} isMultiple: true if search mode is multiple
     */
    updateSelectedSearchedItems(selectedSearchItems, isMultiple) {
      if (!isMultiple) {
        this.selectedSearchedItems = selectedSearchItems;
      }
    },
    /**
     * Add selected search results to search selection.
     * @param {Array} selectedSearchResults: array contains selected items in searchPanel
     */
    addToSearchSelection(selectedSearchResults) {
      if (selectedSearchResults && selectedSearchResults.length > 0) {
        this.selectedSearchedItems.push(...selectedSearchResults);
      }
    },
    /**
     * Set search result and close search panel.
     */
    async setSearchResult() {
      if (!this.selectedSearchedItems || !this.selectedSearchedItems.length) {
        this.closeSearchDialog();
        return;
      }
      const resultUpdate = getPosterService('signages').mapSearchResultsToValues(
        this.selectedSearchedItems,
        this.selectedSearchMode.search,
        this.currentSearchParams.searchDefinition.multiSelect
      );

      const applyTypeChange =
        !this.currentSearchParams.isInput &&
        Object.hasOwnProperty.call(resultUpdate, 'signages_templates_attributes_type') &&
        resultUpdate.signages_templates_attributes_type.value &&
        resultUpdate.signages_templates_attributes_type.value !== this.selectedTypeAlias;
      if (applyTypeChange) {
        // Set type with selected searchresult type if different from current
        await this.setTypeByAlias(resultUpdate.signages_templates_attributes_type.value);
        // Wait for new type, default values to be applied
        await this.updateCurrentSignageType(this.currentType);
      }

      // Apply the values of the mapped search result
      // (overwrite default values applied from type change above)
      Object.entries(resultUpdate).forEach(([alias, update]) => {
        this.setCurrentPosterValue(alias, update.value, null, false, update.updateMode);
        this.doFormAttrCb(alias, attribute => {
          this.startUpdateOn(attribute);
        });
      });

      getPosterService('signages').updateInputsSummary(this.currentSignageItem);
      if (this.currentSearchParams.isInput) {
        // If the attribute that triggered the search is an input, trigger getValues
        this.getValues(true, true);
      }

      this.closeSearchDialog();
    },
    /**
     * Change selected search mode.
     * @param {Object} importMode - New selected search mode
     */
    changeSearchMode(searchMode) {
      this.selectedSearchMode = searchMode;
    },
    /**
     * Checks if the posters are valid then emits the create event
     *
     * @param {String} sendAction - the type of "send" to execute: create, download...
     */
    createPostersHook(sendAction) {
      if (this.importExternalFileCount) {
        services
          .getService('alerts')
          .alertWarning(this.$t('poster.creation.warning.import_external_file_in_progress'));
        return;
      }

      // Check All signage item validity
      for (const poster of this.signageItems) {
        this.checkSignageItem(poster);
      }

      // Partial selection: confirm send
      if (this.selectedSignageItems.length) {
        if (this.$refs.sendSelectionConfirmationDialog) {
          this.$refs.sendSelectionConfirmationDialog.open({
            onCancel: closeDialog => {
              closeDialog();
            },
            onConfirm: closeDialog => {
              closeDialog();
              this.createPostersLimitCheck(sendAction);
            },
          });
        }
      } else {
        // Else send without confirmation
        if (this.nbSignageItemsInvalid > 0) {
          services
            .getService('alerts')
            .alertWarning(this.$t('poster.creation.warning.correct_errors'));
        } else {
          this.createPostersLimitCheck(sendAction);
        }
      }
    },
    /**
     * Starts the poster send limit check
     *
     * @param {string} sendAction - the poster send action
     */
    createPostersLimitCheck(sendAction) {
      // If limit exceeded, confirm limited send
      if (
        this.posterLimit > 0 &&
        ((this.selectedSignageItems.length &&
          this.selectedSignageItems.length > this.posterLimit) ||
          this.signageItems.length > this.posterLimit)
      ) {
        if (this.$refs.sendLimitedCountConfirmationDialog) {
          this.$refs.sendLimitedCountConfirmationDialog.open({
            sendAction,
            onCancel: closeDialog => {
              closeDialog();
            },
            onConfirm: closeDialog => {
              closeDialog();
              this.emitCreation(sendAction);
            },
          });
        }
      } else {
        // Else send without confirmation
        this.emitCreation(sendAction);
      }
    },
    /**
     * Emits the "create" event with the posters
     */
    emitCreation(sendAction) {
      const cart = this.selectedSignageItems.length ? this.selectedSignageItems : this.signageItems;
      const exceedsLimits =
        this.posterLimit > 0 &&
        ((this.selectedSignageItems.length &&
          this.selectedSignageItems.length > this.posterLimit) ||
          this.signageItems.length > this.posterLimit);

      let postersToSend = exceedsLimits ? cart.slice(0, this.posterLimit) : cart;
      postersToSend = postersToSend.filter(s => s.isValid);

      if (this.selectedSignageItems.length) {
        this.$emit('create', sendAction, postersToSend);
        if (this.nbSignageItemsInvalid > 0) {
          services
            .getService('alerts')
            .alertWarning(this.$t('poster.creation.warning.correct_errors'));
        }
      } else {
        this.$emit('create', sendAction, postersToSend);
      }
    },
  },
  /**
   * Mounted hook.
   */
  async mounted() {
    // Load families and types
    this.startLoading();

    try {
      // Preload families
      this.allFamilies = await getPosterService('templates').loadFamilies();

      // Prepare screen with signage items (in the store)
      if (this.signageItems.length > 0) {
        const loadingTypePromises = [];
        for (const signageItem of this.signageItems) {
          // Add signage type loading promise
          loadingTypePromises.push(this.getTypeData(signageItem.type));

          // Rebase signage id counter so ids are constant
          getPosterService('signages').rebaseSignageIdCounter(signageItem.id);
        }

        try {
          await Promise.all(loadingTypePromises);

          // If unset, set the last built family+type to the first poster's
          // so that the quick create shortcut is always available
          if (!this.lastBuiltFamily) {
            const firstPosterFamily = await getPosterService('templates').loadFamily(
              this.signageItems[0].familyId
            );
            this.lastBuiltFamily = firstPosterFamily;
          }
          if (!this.lastBuiltType) {
            const firstPosterType = await getPosterService('templates').loadType(
              this.signageItems[0].type.itemId
            );
            this.lastBuiltType = firstPosterType;
          }

          // Init current signage: restore from 'currentSignageItemId' or select first item
          const currentSignageItem =
            this.signageItems.find(s => s.id === this.currentSignageItemId) || this.signageItems[0];

          if (currentSignageItem != null) {
            this.setCurrentSignageItem(currentSignageItem);
          }
          if (this.signageItems.length > 1) {
            this.showCart = true;
          }
        } catch (err) {
          // Do nothing
        }
      }
    } catch (err) {
      services.getService('alerts').alertError(this.$t('poster.creation.error.load_page_data'));
    }

    this.stopLoading();
  },
  /**
   * Destroyed hook.
   */
  destroyed() {
    RenderTools.clearCaches();
  },
};
</script>
